import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { clearUser } from '../feature/user.Slice';

/**
 * @file
 * @module Pages/AdminConnectionHistory
 * @description Page admin affichant l'historique des connexions sur 48 mois.
 * KPIs pour la direction : connexions totales et utilisateurs uniques par mois.
 */
const apiPrefix = process.env.REACT_APP_API_PREFIX;

const AdminConnectionHistory = () => {
    const token = useSelector((state) => state.user.token);
    const dispatch = useDispatch();
    const [stats, setStats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchHistory = async () => {
            try {
                const response = await fetch(apiPrefix + 'admin/connection-history', {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    if (response.status === 401 || response.status === 403) {
                        dispatch(clearUser());
                    }
                    throw new Error('Erreur lors de la récupération de l\'historique');
                }

                const data = await response.json();
                setStats(data);
            } catch (err) {
                console.error('Erreur:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchHistory();
    }, [apiPrefix, token, dispatch]);

    if (loading) {
        return (
            <div className="container-responsive section-padding">
                <div className="flex justify-center items-center min-h-[200px]">
                    <div className="loading-spinner"></div>
                    <span className="ml-3 text-gray-600">Chargement de l'historique des connexions...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container-responsive section-padding">
                <div className="alert alert-danger">
                    <i className="fas fa-exclamation-triangle mr-2"></i>
                    {error}
                </div>
                <Link to="/dashboard" className="btn-secondary inline-flex items-center mt-4">
                    <i className="fas fa-arrow-left mr-2"></i>
                    Retour au tableau de bord
                </Link>
            </div>
        );
    }

    const totalConnections = stats.reduce((sum, s) => sum + (s.totalConnections || 0), 0);
    const maxConnections = Math.max(...stats.map(s => s.totalConnections || 0), 1);

    return (
        <div className="container-responsive section-padding">
            <div className="max-w-6xl mx-auto">
                <div className="mb-6">
                    <Link
                        to="/dashboard"
                        className="btn-secondary inline-flex items-center"
                    >
                        <i className="fas fa-arrow-left mr-2"></i>
                        Retour au tableau de bord
                    </Link>
                </div>

                <div className="card mb-6">
                    <h1 className="text-3xl font-bold text-primary mb-4">
                        <i className="fas fa-chart-line mr-2"></i>
                        Historique des connexions (48 mois)
                    </h1>
                    <div className="alert alert-info">
                        <i className="fas fa-info-circle mr-2"></i>
                        Historique des connexions par mois. Les données sont enregistrées à chaque connexion utilisateur.
                    </div>
                </div>

                {stats.length === 0 ? (
                    <div className="card">
                        <p className="text-gray-600 text-center py-8">
                            <i className="fas fa-database text-4xl text-gray-300 mb-4 block"></i>
                            Aucune donnée de connexion enregistrée pour le moment.
                            L'historique se remplira au fur et à mesure des connexions des utilisateurs.
                        </p>
                    </div>
                ) : (
                    <div className="card">
                        <div className="mb-4 flex flex-wrap gap-4">
                            <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded">
                                <span className="text-sm text-gray-600">Total connexions (période)</span>
                                <p className="text-xl font-bold text-blue-600">{totalConnections.toLocaleString()}</p>
                            </div>
                            <div className="bg-green-50 border-l-4 border-green-500 p-3 rounded">
                                <span className="text-sm text-gray-600">Mois avec données</span>
                                <p className="text-xl font-bold text-green-600">{stats.length}</p>
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Mois</th>
                                        <th className="text-right">Connexions totales</th>
                                        <th className="text-right">Utilisateurs uniques</th>
                                        <th className="w-48">Répartition</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stats.map((s, index) => (
                                        <tr key={`${s.year}-${s.month}-${index}`}>
                                            <td className="font-medium">{s.label}</td>
                                            <td className="text-right">{s.totalConnections?.toLocaleString() ?? 0}</td>
                                            <td className="text-right">{s.uniqueUsers?.toLocaleString() ?? 0}</td>
                                            <td>
                                                <div className="w-full bg-gray-200 rounded-full h-2">
                                                    <div
                                                        className="bg-primary h-2 rounded-full transition-all"
                                                        style={{ width: `${Math.min(100, (s.totalConnections / maxConnections) * 100)}%` }}
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminConnectionHistory;
